import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BlurText from './BlurText';

const pricingTiers = [
  { minGb: 1, maxGb: 4, d30: 1.8000, d7: 1.6000, d1: 1.4000 },
  { minGb: 5, maxGb: 9, d30: 1.6920, d7: 1.5040, d1: 1.3160 },
  { minGb: 10, maxGb: 49, d30: 1.6110, d7: 1.4320, d1: 1.2530 },
  { minGb: 50, maxGb: 99, d30: 1.4490, d7: 1.2880, d1: 1.1270 },
  { minGb: 100, maxGb: 249, d30: 1.2870, d7: 1.1440, d1: 1.0010 },
  { minGb: 250, maxGb: 499, d30: 1.0710, d7: 0.9520, d1: 0.8330 },
  { minGb: 500, maxGb: 999, d30: 0.8820, d7: 0.7840, d1: 0.6860 },
];

const durations = [
  { key: 'd1', label: '1 Day' },
  { key: 'd7', label: '7 Days' },
  { key: 'd30', label: '30 Days' },
];

const Features = () => {
  const [activeDuration, setActiveDuration] = useState('d30');
  const [activeSlide, setActiveSlide] = useState(0);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const getPrice = (tier) => tier[activeDuration];

  const discount = (tier) => {
    const basePrice = pricingTiers[0][activeDuration];
    const currentPrice = getPrice(tier);
    const pct = Math.round((1 - currentPrice / basePrice) * 100);
    return pct > 0 ? pct : null;
  };

  const allCards = [
    ...pricingTiers.map((tier, i) => ({ type: 'tier', tier, i })),
    { type: 'custom' },
  ];

  const totalSlides = Math.ceil(allCards.length / 2);

  const goToSlide = (index) => {
    setActiveSlide(index);
    if (scrollRef.current) {
      const slideWidth = scrollRef.current.offsetWidth;
      scrollRef.current.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const slideWidth = scrollRef.current.offsetWidth;
      const newSlide = Math.round(scrollRef.current.scrollLeft / slideWidth);
      if (newSlide !== activeSlide) {
        setActiveSlide(newSlide);
      }
    }
  };

  const renderCard = (card) => {
    if (card.type === 'custom') {
      return (
        <div
          key="custom"
          className="pricing-card pricing-card-custom"
          onClick={() => navigate('/dashboard/bandwidth')}
        >
          <div className="pricing-card-range">Custom</div>
          <div className="pricing-card-custom-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>
          <div className="pricing-card-per">Choose your own amount</div>
        </div>
      );
    }

    const { tier, i } = card;
    const disc = discount(tier);
    return (
      <div className="pricing-card" key={i} onClick={() => navigate('/dashboard/bandwidth')}>
        {disc && <span className="pricing-card-badge">-{disc}%</span>}
        <div className="pricing-card-range">
          {tier.minGb} — {tier.maxGb} GB
        </div>
        <div className="pricing-card-price">
          <span className="pricing-card-amount">${getPrice(tier).toFixed(2)}</span>
        </div>
        <div className="pricing-card-per">per GB</div>
        <div className="pricing-card-arrow">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </div>
      </div>
    );
  };

  // Group cards into pairs for mobile slides
  const slides = [];
  for (let i = 0; i < allCards.length; i += 2) {
    slides.push(allCards.slice(i, i + 2));
  }

  return (
    <section className="features-section" id="features">
      <div className="container">
        <div className="features-header">
          <p className="features-label-top">MONTHLY PACKAGES</p>
          <h2 className="features-title">
            <BlurText text="Residential Basic Pricing" delay={62} direction="top" />
          </h2>
          <p className="features-subtitle">Volume-based pricing — the more you buy, the less you pay per GB</p>
        </div>

        <div className="pricing-duration-tabs">
          {durations.map((d) => (
            <button
              key={d.key}
              className={`pricing-duration-tab${activeDuration === d.key ? ' active' : ''}`}
              onClick={() => setActiveDuration(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Desktop grid */}
        <div className="pricing-cards-grid pricing-desktop">
          {allCards.map((card) => renderCard(card))}
        </div>

        {/* Mobile carousel */}
        <div className="pricing-mobile">
          <div
            className="pricing-carousel"
            ref={scrollRef}
            onScroll={handleScroll}
          >
            {slides.map((slideCards, si) => (
              <div className="pricing-carousel-slide" key={si}>
                {slideCards.map((card) => renderCard(card))}
              </div>
            ))}
          </div>
          <div className="pricing-carousel-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`pricing-carousel-dot${activeSlide === i ? ' active' : ''}`}
                onClick={() => goToSlide(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
